import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { message, Spin } from 'antd';
import { CheckIcon } from '@heroicons/react/24/outline';
import PageHeader from '../../components/ui/page-header';
import TWConfirmModal from '../../components/ui/tw-confirm-modal';
import { TWButton } from '../../components/ui';
import menuImportApi from '../../services/menuImportApi';
import squareCatalogApi from '../../services/squareCatalogApi';
import { SquareImportStatus } from '../../data/enums';
import squareErrorToast from '../../components/menu/square-shared/squareErrorToast';
import SquareReconnectStrip from '../../components/menu/square-shared/reconnect-strip';
import { capitalizeWords } from '../../utils/baseUtils';
import UploadStep from './components/UploadStep';
import ProcessingStep from './components/ProcessingStep';
import ReviewStep from './components/ReviewStep';

const STEPS = [
  { id: 1, name: 'Upload' },
  { id: 2, name: 'Processing' },
  { id: 3, name: 'Review' },
];

const SQUARE_POLL_START_MS = 3000;
const SQUARE_POLL_MAX_MS = 10000;

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const MenuImport = () => {
  const navigate = useNavigate();
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const companyId = globalCompany?.id;

  const [currentStep, setCurrentStep] = useState(1);
  const [jobId, setJobId] = useState(null);
  const [menuData, setMenuData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // 'upload' drives the existing file->AI-parse->review pipeline; 'square'
  // drives the Square catalog import path added alongside it. Steps 2/3
  // render different markup depending on which source is active.
  const [importSource, setImportSource] = useState('upload');
  const [squareJobId, setSquareJobId] = useState(null);

  const squareCancelledRef = useRef(false);
  const squarePollTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      squareCancelledRef.current = true;
      if (squarePollTimeoutRef.current) clearTimeout(squarePollTimeoutRef.current);
    };
  }, []);

  const resetToUpload = () => {
    setImportSource('upload');
    setSquareJobId(null);
    setCurrentStep(1);
  };

  const pollSquareImport = (id, delay = SQUARE_POLL_START_MS) => {
    squarePollTimeoutRef.current = setTimeout(async () => {
      if (squareCancelledRef.current) return;
      try {
        const res = await squareCatalogApi.importStatus(id);
        if (squareCancelledRef.current) return;
        const data = res?.data?.data;
        if (!data) return;
        if (data.status === SquareImportStatus.Done) {
          setCurrentStep(3);
          return;
        }
        if (data.status === SquareImportStatus.Failed) {
          message.error(data.errormessage || 'Square import failed');
          resetToUpload();
          return;
        }
        pollSquareImport(id, Math.min(delay + 1000, SQUARE_POLL_MAX_MS));
      } catch (err) {
        if (squareCancelledRef.current) return;
        squareErrorToast(err);
        resetToUpload();
      }
    }, delay);
  };

  const handleUploaded = (id) => {
    setImportSource('upload');
    setJobId(id);
    setCurrentStep(2);
  };

  const handleSquareImportStarted = (id) => {
    setImportSource('square');
    setJobId(null);
    setSquareJobId(id);
    setCurrentStep(2);
    pollSquareImport(id);
  };

  const normalizeMenuCasing = (parsed) => {
    if (!parsed) return parsed;
    return {
      ...parsed,
      menu: { ...(parsed.menu || {}), name: 'Menu' },
      categories: (parsed.categories || []).map((category) => ({
        ...category,
        name: capitalizeWords(category.name),
        products: (category.products || []).map((product) => {
          const productName = capitalizeWords(product.name);
          return {
            ...product,
            name: productName,
            items: (product.items || []).map((item) => {
              const rawName = (item.name || '').trim();
              const hasRealVariant =
                rawName && rawName.toLowerCase() !== 'regular';
              return {
                ...item,
                name: hasRealVariant ? capitalizeWords(rawName) : productName,
              };
            }),
          };
        }),
      })),
    };
  };

  const handleProcessingReady = (statusData) => {
    setMenuData(normalizeMenuCasing(statusData.parsedmenu));
    setCurrentStep(3);
  };

  const handleProcessingFailed = (msg) => {
    message.error(typeof msg === 'string' ? msg : 'Menu processing failed');
    setCurrentStep(1);
    setJobId(null);
  };

  const handleConfirmImport = async () => {
    if (!jobId || !menuData?.menu?.name?.trim()) {
      message.error('Menu name is required');
      return;
    }
    setConfirming(true);
    try {
      await menuImportApi.confirm({
        jobid: jobId,
        menu: menuData,
      });
      message.success('Menu imported successfully');
      setConfirmOpen(false);
      navigate('/dashboard/menus');
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err?.response?.data ||
          err?.message ||
          'Import failed'
      );
    } finally {
      setConfirming(false);
    }
  };

  if (!companyId) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <div className="mx-auto max-w-5xl text-sm text-slate-600 dark:text-slate-300">
          Select a company first to import a menu.
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <SquareReconnectStrip />
        <PageHeader
          title="Import Menu"
          description="Upload an image or PDF, review AI results, then import"
        />

        <nav
          className="flex flex-wrap justify-center gap-4"
          aria-label="Progress"
        >
          {STEPS.map((step) => (
            <div key={step.id} className="flex items-center gap-2">
              <span
                className={classNames(
                  'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold',
                  currentStep > step.id
                    ? 'bg-emerald-600 text-white'
                    : currentStep === step.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-600 dark:bg-slate-600 dark:text-slate-300',
                )}
              >
                {currentStep > step.id ? (
                  <CheckIcon className="h-4 w-4" />
                ) : (
                  step.id
                )}
              </span>
              <span
                className={classNames(
                  'text-sm font-medium',
                  currentStep >= step.id
                    ? 'text-slate-900 dark:text-white'
                    : 'text-slate-400',
                )}
              >
                {step.name}
              </span>
            </div>
          ))}
        </nav>

        <div>
          {currentStep === 1 && (
            <UploadStep
              companyId={companyId}
              onUploaded={handleUploaded}
              onSquareImportStarted={handleSquareImportStarted}
              onError={(msg) =>
                message.error(typeof msg === 'string' ? msg : 'Upload failed')
              }
            />
          )}
          {currentStep === 2 && importSource === 'upload' && jobId && (
            <ProcessingStep
              jobId={jobId}
              onReady={handleProcessingReady}
              onFailed={handleProcessingFailed}
            />
          )}
          {currentStep === 2 && importSource === 'square' && squareJobId && (
            <div className="mx-auto max-w-lg rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-slate-700 dark:bg-slate-800">
              <Spin size="large" />
              <h2 className="mt-6 text-lg font-semibold text-slate-900 dark:text-white">
                Importing from Square
              </h2>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Pulling your catalog and building a draft menu...
              </p>
            </div>
          )}
          {currentStep === 3 && importSource === 'square' && (
            <div className="mx-auto max-w-lg rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm dark:border-slate-700 dark:bg-slate-800">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400">
                <CheckIcon className="h-6 w-6" />
              </span>
              <h2 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">
                Draft menu created.
              </h2>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Review it in Menu Editor before publishing to your stores.
              </p>
              <TWButton
                variant="primary"
                className="mt-6"
                onClick={() => navigate('/dashboard/menus')}
              >
                Go to Menus
              </TWButton>
            </div>
          )}
          {currentStep === 3 && importSource === 'upload' && menuData && (
            <ReviewStep
              menuData={menuData}
              setMenuData={setMenuData}
              confirming={confirming}
              onConfirm={() => setConfirmOpen(true)}
            />
          )}
        </div>
      </div>

      <TWConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmImport}
        title="Import menu to system?"
        description={`This will create "${menuData?.menu?.name}" with all categories and products in your account. You can edit them later in Menu Editor.`}
        confirmLabel="Import"
        tone="info"
        loading={confirming}
      />
    </div>
  );
};

export default MenuImport;
